import { useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import type { Incident } from '../data/demoData';

type Decision = 'approve' | 'reject' | 'defer';

type AutopilotAction = {
  id: string;
  action: string;
  label: string;
  target: string;
  targetType: string;
  priority: 'Immediate' | 'High' | 'Medium' | 'Low';
  containmentBenefit: string;
  businessImpact: string;
  dependencyRisk: string;
  requiredPermissions: string[];
  rollbackSteps: string[];
  approvalRequired: boolean;
  approvalLevel: string;
  rationale: string;
};

type AutopilotResult = {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  likelyRootCause: string;
  affectedAssetsUsers: string[];
  mitreChain: string[];
  blastRadius: { level: string; summary: string; entities: string[] };
  recommendedActions: AutopilotAction[];
  confidenceScore: number;
  confidenceLabel: string;
  falsePositiveRisk: string;
  automationDecision: string;
  expectedBusinessImpact: string;
  rollbackPlan: string[];
  approval: { required: boolean; level: string; reason: string };
  aiVerdict: { usedAi: boolean; model: string; summary: string; markdown: string };
  diagnostics: string[];
};

interface IncidentAutopilotPanelProps {
  incident: Incident;
  customerId: string;
}

function getIncidentId(incident: Incident): string {
  return String((incident as unknown as Record<string, unknown>).armIncidentName ?? incident.id ?? incident.incidentNumber ?? '').trim();
}

function toneFor(value: string): string {
  const v = value.toLowerCase();
  if (v.includes('critical') || v.includes('immediate') || v.includes('customer') || v.includes('hold')) return 'border-red-300 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200';
  if (v.includes('high') || v.includes('approval')) return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-200';
  if (v.includes('medium')) return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400 dark:bg-amber-500/10 dark:text-amber-200';
  return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/10 dark:text-emerald-200';
}

function renderMarkdownLite(markdown: string) {
  return markdown.split(/\r?\n/).map((line, idx) => {
    const key = `${idx}-${line.slice(0, 16)}`;
    if (!line.trim()) return <div key={key} className="h-2" />;
    if (line.startsWith('### ')) return <h4 key={key} className="mt-3 text-sm font-black text-foreground">{line.slice(4)}</h4>;
    if (line.startsWith('## ')) return <h3 key={key} className="mt-3 text-base font-black text-foreground">{line.slice(3)}</h3>;
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line.trim());
    if (bullet) return <div key={key} className="ml-3 text-sm text-muted-foreground">• {bullet[1].replace(/\*\*/g, '')}</div>;
    return <p key={key} className="text-sm text-muted-foreground">{line.replace(/\*\*/g, '')}</p>;
  });
}

export function IncidentAutopilotPanel({ incident, customerId }: IncidentAutopilotPanelProps) {
  const { canCapability } = useAuth();
  const [result, setResult] = useState<AutopilotResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const canApprove = canCapability('incidents', 'approve_autopilot');
  const incidentId = useMemo(() => getIncidentId(incident), [incident]);

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<AutopilotResult>(`/incidents/${encodeURIComponent(incidentId)}/autopilot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, incident, analystNote: note }),
      });
      setResult(data);
      setSelected(new Set(data.recommendedActions.map((action) => action.id)));
      toast({ title: 'SOC Copilot Autopilot ready', description: `${data.automationDecision} · Confidence ${data.confidenceScore}%` });
    } catch (err) {
      const msg = err instanceof ApiError && err.forbidden
        ? 'Permission denied. Enable Incidents → Use SOC Copilot Autopilot in Permissions Master.'
        : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function decide(decision: Decision) {
    if (!result || !canApprove) return;
    setDeciding(decision);
    setError(null);
    try {
      await apiFetch(`/incidents/${encodeURIComponent(incidentId)}/autopilot/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, decision, actionIds: [...selected], note, recommendation: { confidenceScore: result.confidenceScore, automationDecision: result.automationDecision } }),
      });
      toast({ title: `Autopilot recommendation ${decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'deferred'}`, description: `${selected.size} action(s) were recorded in the audit trail.` });
    } catch (err) {
      const msg = err instanceof ApiError && err.forbidden
        ? 'Permission denied. Enable Incidents → Approve / Reject Autopilot in Permissions Master.'
        : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setDeciding(null);
    }
  }

  function toggleAction(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="mt-4 rounded-2xl border border-blue-300/40 bg-blue-950/10 p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">SOC Copilot Autopilot</div>
          <h3 className="text-lg font-black text-foreground">Autopilot Recommendation Engine</h3>
          <p className="text-sm text-muted-foreground max-w-4xl">Generates a human-approved containment plan with root cause, MITRE chain, blast radius, confidence, business impact, rollback and approval guidance. It does not execute containment automatically.</p>
        </div>
        <button type="button" onClick={() => void generate()} disabled={loading} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60">
          {loading ? 'Generating…' : result ? 'Regenerate' : 'Generate Autopilot'}
        </button>
      </div>

      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Optional analyst note: known VIP/service account, maintenance window, customer approval constraints, business-critical asset context…"
        className="min-h-[70px] w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
      />

      {error && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}

      {result && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground font-bold">Decision</div><div className="mt-2 text-xl font-black text-foreground">{result.automationDecision}</div></div>
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground font-bold">Confidence</div><div className="mt-2 text-3xl font-black text-foreground">{result.confidenceScore}%</div><div className="text-xs text-muted-foreground">{result.confidenceLabel}</div></div>
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground font-bold">False Positive Risk</div><div className="mt-2 text-xl font-black text-foreground">{result.falsePositiveRisk}</div></div>
            <div className={`rounded-xl border p-4 ${toneFor(result.approval.level)}`}><div className="text-xs uppercase font-bold">Approval</div><div className="mt-2 text-xl font-black">{result.approval.required ? result.approval.level : 'Not required'}</div></div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_0.9fr]">
            <div className="rounded-xl border border-border bg-card p-4">
              <h4 className="font-black text-foreground">Root Cause & Blast Radius</h4>
              <p className="mt-2 text-sm text-muted-foreground">{result.likelyRootCause}</p>
              <div className="mt-3 rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground"><strong className="text-foreground">Blast radius:</strong> {result.blastRadius.summary}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {result.affectedAssetsUsers.slice(0, 16).map((item) => <span key={item} className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground">{item}</span>)}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <h4 className="font-black text-foreground">MITRE / Business Impact</h4>
              <div className="mt-2 flex flex-wrap gap-2">{result.mitreChain.map((item) => <span key={item} className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground">{item}</span>)}</div>
              <p className="mt-3 text-sm text-muted-foreground">{result.expectedBusinessImpact}</p>
              <p className="mt-3 text-xs text-muted-foreground">{result.approval.reason}</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-black text-foreground">Recommended Containment Actions</h4>
              <div className="text-xs text-muted-foreground">{selected.size} selected for decision</div>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {result.recommendedActions.map((action) => (
                <label key={action.id} className="rounded-xl border border-border bg-background p-3 cursor-pointer hover:bg-muted/40">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selected.has(action.id)} onChange={() => toggleAction(action.id)} className="mt-1" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${toneFor(action.priority)}`}>{action.priority}</span>
                        <span className="font-bold text-foreground">{action.label}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">Target: {action.targetType} · {action.target}</div>
                      <p className="mt-2 text-sm text-muted-foreground">{action.rationale}</p>
                      <div className="mt-2 grid gap-2 text-xs text-muted-foreground md:grid-cols-3"><div>{action.containmentBenefit}</div><div>{action.businessImpact}</div><div>{action.dependencyRisk}</div></div>
                      <details className="mt-2 text-xs text-muted-foreground"><summary className="cursor-pointer font-bold text-foreground">Permissions & rollback</summary><ul className="mt-1 list-disc pl-5"><li>Permissions: {action.requiredPermissions.join(', ') || 'N/A'}</li>{action.rollbackSteps.map((step) => <li key={step}>{step}</li>)}</ul></details>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h4 className="font-black text-foreground">AI Verdict</h4>
            <div className="mt-2 space-y-1 rounded-xl border border-border bg-background p-3">{renderMarkdownLite(result.aiVerdict.markdown)}</div>
            <div className="mt-2 text-xs text-muted-foreground">Model: {result.aiVerdict.model}{result.aiVerdict.usedAi ? '' : ' · deterministic fallback'}</div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {!canApprove && <span className="text-xs text-muted-foreground">Approval buttons are hidden until Incidents → Approve / Reject Autopilot is enabled.</span>}
            {canApprove && <button type="button" disabled={!!deciding || selected.size === 0} onClick={() => void decide('defer')} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{deciding === 'defer' ? 'Recording…' : 'Defer'}</button>}
            {canApprove && <button type="button" disabled={!!deciding || selected.size === 0} onClick={() => void decide('reject')} className="rounded-xl border border-red-300 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-60">{deciding === 'reject' ? 'Recording…' : 'Reject'}</button>}
            {canApprove && <button type="button" disabled={!!deciding || selected.size === 0} onClick={() => void decide('approve')} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">{deciding === 'approve' ? 'Recording…' : 'Approve Selected'}</button>}
          </div>
        </>
      )}
    </div>
  );
}
